import { useMemo } from "react";
import { History, Visibility } from "@mui/icons-material";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";

import { PatientSupportingDocumentRecord } from "./types";

type PatientSupportingDocumentHistoryDialogProps = {
  open: boolean;
  onClose: () => void;
  /** Título do tipo (ex.: "Receita médica"). */
  typeTitle?: string;
  /** Documentos ativos (vigentes) do tipo. */
  activeDocuments?: PatientSupportingDocumentRecord[];
  /** Documentos removidos (docStatus=cancelled) do tipo. */
  cancelledDocuments?: PatientSupportingDocumentRecord[];
  onView: (document: PatientSupportingDocumentRecord) => void;
};

function isCancelled(doc: PatientSupportingDocumentRecord): boolean {
  return String(doc.docStatus ?? "").toLowerCase() === "cancelled";
}

/** Data em que o paciente enviou o arquivo. */
function getSentAt(doc: PatientSupportingDocumentRecord): string {
  return doc.sentAt || doc.updatedAt || "";
}

/** Data da remoção lógica (meta.lastUpdated / removedAt). */
function getRemovedAt(doc: PatientSupportingDocumentRecord): string {
  if (!isCancelled(doc)) return "";
  return doc.removedAt || "";
}

function formatDateTime(raw: string | undefined): string {
  if (!raw) return "—";
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY [às] HH:mm") : "—";
}

/** Ordena pelo evento mais recente (remoção ou envio). */
function sortKey(doc: PatientSupportingDocumentRecord): number {
  const removedAt = getRemovedAt(doc);
  const sentAt = getSentAt(doc);
  return Date.parse(removedAt || sentAt || "0");
}

/**
 * Histórico do tipo: vigentes + removidos.
 * Distingue data de envio e data de remoção (meta.lastUpdated).
 */
export function PatientSupportingDocumentHistoryDialog({
  open,
  onClose,
  typeTitle,
  activeDocuments = [],
  cancelledDocuments = [],
  onView,
}: PatientSupportingDocumentHistoryDialogProps) {
  const title = typeTitle
    ? `Histórico de documentos — ${typeTitle}`
    : "Histórico de documentos";

  const historyRows = useMemo(() => {
    const combined = [
      ...activeDocuments.map((doc) => ({
        ...doc,
        docStatus: doc.docStatus ?? "final",
      })),
      ...cancelledDocuments.map((doc) => ({
        ...doc,
        docStatus: doc.docStatus ?? "cancelled",
      })),
    ];
    return combined.sort((a, b) => sortKey(b) - sortKey(a));
  }, [activeDocuments, cancelledDocuments]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <History color="primary" />
          <Typography variant="h6" component="span" fontSize="1.1rem">
            {title}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {!historyRows.length ? (
          <Typography variant="body2" color="text.secondary">
            Nenhum documento neste tipo.
          </Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Documento</TableCell>
                <TableCell>Enviado em</TableCell>
                <TableCell>Removido em</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Ação</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historyRows.map((doc) => {
                const removed = isCancelled(doc);
                const sentAt = getSentAt(doc);
                const removedAt = getRemovedAt(doc);

                return (
                  <TableRow
                    key={
                      doc.documentReferenceId ??
                      `${doc.fileName}-${sentAt}-${doc.docStatus}`
                    }
                    hover
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {doc.description || doc.fileName}
                      </Typography>
                      {doc.description ? (
                        <Typography variant="caption" color="text.secondary">
                          {doc.fileName}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Data em que o documento foi enviado">
                        <Typography variant="body2" component="span">
                          {formatDateTime(sentAt)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {removed ? (
                        <Tooltip title="Data em que o documento foi removido">
                          <Typography variant="body2" component="span">
                            {formatDateTime(removedAt)}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          component="span"
                        >
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{removed ? "Removido" : "Vigente"}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Visualizar documento">
                        <IconButton
                          aria-label={`Visualizar ${doc.fileName}`}
                          onClick={() => onView(doc)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
